/**
 * Widget Preview Form - Isolated Form Component
 *
 * A fork of PreviewForm that uses only eg-* prefixed components
 * without any Bootstrap or react-router dependencies.
 * This ensures complete style isolation when embedded in external sites.
 */

import { useEffect, useRef, useState } from "react";
import _ from "lodash";
import Swal from "sweetalert2";
import ReactDOMServer from 'react-dom/server';

// Import isolated components
import {
  EgContainer,
  EgRow,
  EgCol,
  EgButton,
  EgStepper,
  EgTabs,
  EgTabList,
  EgTab,
  EgTabPanels,
  EgTabPanel
} from './components/WidgetGrid';

import {
  EgInput,
  EgSelect,
  EgCheckbox,
  EgLabel,
  EgFormGroup,
  EgErrorMessage
} from './components/WidgetInputs';

// Import form utilities (these don't have Bootstrap dependencies)
import {
  CATALOGUES_PREFIX,
  INPUT_TYPE_CATALOGUE_ERROR,
  INPUT_TYPE_CATALOGUE_RADIO,
  INPUT_TYPE_CATALOGUE_RADIO_CONDITIONAL,
  INPUT_TYPE_CATALOGUE_RADIO_DESCRIPTION,
  INPUT_TYPE_CATALOGUE_RADIO_DESCRIPTION_CONDITIONAL,
  INPUT_TYPE_CATALOGUE_SELECT,
  INPUT_TYPE_CATALOGUE_SELECT_CONDITIONAL,
  INPUT_TYPE_CHECKBOX,
  INPUT_TYPE_CHECKBOX_CONDITIONAL,
  INPUT_TYPE_DATE,
  INPUT_TYPE_DATE_RANGE,
  INPUT_TYPE_DESCRIPTION,
  INPUT_TYPE_EMAIL,
  INPUT_TYPE_FILE,
  INPUT_TYPE_NUMBER,
  INPUT_TYPE_SUBJECT,
  INPUT_TYPE_TEXT,
  INPUT_TYPE_TEXTAREA,
  INPUT_TYPE_INVOLVED,
  isForLanding,
  isEspecialCatalogue
} from "../components/Form/consts";

// Import i18n from original
import I18nText from "../components/Form/PreviewForm/i18n";

// Widget API
import { createWidgetApiClient } from "./widget-api";

// Images
import ReportRobot from "../assets/images/reportrobot.png";

// Base64 encoded SVG icons to avoid CSS reset issues
const WIDGET_ICONS = {
  // Gallery/Image icon - gray color #8aa1ac
  gallery: "data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAyNCAyNCIgd2lkdGg9IjM1IiBoZWlnaHQ9IjM1Ij48cGF0aCBkPSJNMjEgMTlWNWMwLTEuMS0uOS0yLTItMkg1Yy0xLjEgMC0yIC45LTIgMnYxNGMwIDEuMS45IDIgMiAyaDE0YzEuMSAwIDItLjkgMi0yek04LjUgMTMuNWwyLjUgMy4wMUwxNC41IDEybDQuNSA2SDVsMy41LTQuNXoiIGZpbGw9IiM4YWExYWMiLz48L3N2Zz4=",
  // Document icon
  document: "data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAyNCAyNCIgd2lkdGg9IjM1IiBoZWlnaHQ9IjM1Ij48cGF0aCBkPSJNMTQgMkg2Yy0xLjEgMC0xLjk5LjktMS45OSAyTDQgMjBjMCAxLjEuODkgMiAxLjk5IDJIMThjMS4xIDAgMi0uOSAyLTJWOGwtNi02em0yIDE2SDh2LTJoOHYyem0wLTRIOHYtMmg4djJ6bS0zLTVWMy41TDE4LjUgOUgxM3oiIGZpbGw9IiM4YWExYWMiLz48L3N2Zz4=",
  // Video icon
  video: "data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAyNCAyNCIgd2lkdGg9IjM1IiBoZWlnaHQ9IjM1Ij48cGF0aCBkPSJNMTcgMTAuNVY3YzAtLjU1LS40NS0xLTEtMUg0Yy0uNTUgMC0xIC40NS0xIDF2MTBjMCAuNTUuNDUgMSAxIDFoMTJjLjU1IDAgMS0uNDUgMS0xdi0zLjVsNCA0di0xMWwtNCA0eiIgZmlsbD0iIzhhYTFhYyIvPjwvc3ZnPg==",
  // Audio icon
  audio: "data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAyNCAyNCIgd2lkdGg9IjM1IiBoZWlnaHQ9IjM1Ij48cGF0aCBkPSJNMyA5djZoNGw1IDVWNEY3IDlIM3ptMTMuNSAzYzAtMS43Ny0xLjAyLTMuMjktMi41LTQuMDN2OC4wNWMxLjQ4LS43MyAyLjUtMi4yNSAyLjUtNC4wMnpNMTQgMy4yM3YyLjA2YzIuODkuODYgNSAzLjU0IDUgNi43MXMtMi4xMSA1Ljg1LTUgNi43MXYyLjA2YzQuMDEtLjkxIDctNC40OSA3LTguNzdzLTIuOTktNy44Ni03LTguNzd6IiBmaWxsPSIjOGFhMWFjIi8+PC9zdmc+",
  // Clipboard icon
  clipboard: "data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAyNCAyNCIgd2lkdGg9IjM1IiBoZWlnaHQ9IjM1Ij48cGF0aCBkPSJNMTkgMmgtNC4xOEMxNC40Ljg0IDEzLjMgMCAxMiAwYy0xLjMgMC0yLjQuODQtMi44MiAySDVjLTEuMSAwLTIgLjktMiAydjE2YzAgMS4xLjkgMiAyIDJoMTRjMS4xIDAgMi0uOSAyLTJWNGMwLTEuMS0uOS0yLTItMnptLTcgMGMuNTUgMCAxIC40NSAxIDFzLS40NSAxLTEgMS0xLS40NS0xLTEgLjQ1LTEgMS0xem03IDE4SDVWNGgydjNoMTBWNGgydjE2eiIgZmlsbD0iIzhhYTFhYyIvPjwvc3ZnPg==",
  // Eye slash icon - yellow/orange color for hidden fields
  eye_slash: "data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAyNCAyNCIgd2lkdGg9IjE2IiBoZWlnaHQ9IjE2Ij48cGF0aCBkPSJNMTIgN2MyLjc2IDAgNSAyLjI0IDUgNSAwIC42NS0uMTMgMS4yNi0uMzYgMS44M2wyLjkyIDIuOTJjMS41MS0xLjI2IDIuNy0yLjg5IDMuNDMtNC43NS0xLjczLTQuMzktNi03LjUtMTEtNy41LTEuNCAwLTIuNzQuMjUtMy45OC43bDIuMTYgMi4xNkMxMC43NCA3LjEzIDExLjM1IDcgMTIgN3pNMiA0LjI3bDIuMjggMi4yOC40Ni40NkMzLjA4IDguMyAxLjc4IDEwLjAyIDEgMTJjMS43MyA0LjM5IDYgNy41IDExIDcuNSAxLjU1IDAgMy4wMy0uMyA0LjM4LS44NGwuNDIuNDJMMTkuNzMgMjIgMjEgMjAuNzMgMy4yNyAzIDIgNC4yN3pNNy41MyA5LjhsMS41NSAxLjU1Yy0uMDUuMjEtLjA4LjQzLS4wOC42NSAwIDEuNjYgMS4zNCAzIDMgMyAuMjIgMCAuNDQtLjAzLjY1LS4wOGwxLjU1IDEuNTVjLS42Ny4zMy0xLjQxLjUzLTIuMi41My0yLjc2IDAtNS0yLjI0LTUtNSAwLS43OS4yLTEuNTMuNTMtMi4yem00LjMxLS43OGwzLjE1IDMuMTUuMDItLjE2YzAtMS42Ni0xLjM0LTMtMy0zbC0uMTcuMDF6IiBmaWxsPSIjZjE5YTE1Ii8+PC9zdmc+",
  // Delete/X icon - red
  delete: "data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAyNCAyNCIgd2lkdGg9IjE2IiBoZWlnaHQ9IjE2Ij48cGF0aCBkPSJNMTkgNi40MUwxNy41OSA1IDEyIDEwLjU5IDYuNDEgNSA1IDYuNDEgMTAuNTkgMTIgNSAxNy41OSA2LjQxIDE5IDEyIDEzLjQxIDE3LjU5IDE5IDE5IDE3LjU5IDEzLjQxIDEyIDE5IDYuNDF6IiBmaWxsPSIjZGMzNTQ1Ii8+PC9zdmc+"
};

// Icon component using base64 images
const WidgetIcon = ({ name, className = "", size = 35 }) => {
  const iconSrc = WIDGET_ICONS[name];
  if (!iconSrc) return null;

  return (
    <img
      src={iconSrc}
      alt=""
      className={className}
      style={{
        display: 'inline-block',
        width: `${size}px`,
        height: `${size}px`,
        verticalAlign: 'middle'
      }}
    />
  );
};

// Helper functions from original PreviewForm
export const hdlSchm = ({
  action,
  path,
  selected,
  children,
  pathSchema,
  inputs,
  entireSchema,
  setSchemaState,
}) => {
  const putAllPreviousLastChildOnFalse = (schema) => {
    let nextCount = path.split(".");
    nextCount = nextCount.filter((i) => i === "next").length;
    for (let index = 0; index <= nextCount; index++) {
      _.set(schema, `${pathSchema}.${index > 0 ? "next.".repeat(index) : ""}lastChild`, false);
    }
  };

  const newSchema = _.cloneDeep(entireSchema);
  if (action === "selection") {
    _.unset(newSchema, `${pathSchema}.${path}next`);
    putAllPreviousLastChildOnFalse(newSchema);
    _.set(newSchema, `${pathSchema}.${path}lastChild`, false);
    _.set(newSchema, `${pathSchema}.${path}selected`, selected);
    _.set(newSchema, `${pathSchema}.${path}next.children`, children);
    _.set(newSchema, `${pathSchema}.${path}next.selected`, "");
    _.set(newSchema, `${pathSchema}.${path}next.lastChild`, true);
  }
  if (action === "nullSelection") {
    _.set(newSchema, `${pathSchema}.${path}selected`, "");
    _.set(newSchema, `${pathSchema}.${path}lastChild`, false);
    _.unset(newSchema, `${pathSchema}.${path}next`);
  }
  if (action === "noNextPath") {
    _.set(newSchema, `${pathSchema}.${path}selected`, selected);
    _.set(newSchema, `${pathSchema}.${path}lastChild`, true);
    _.unset(newSchema, `${pathSchema}.${path}next`);
  }
  if (action === "resetInputs") {
    _.set(newSchema, `${pathSchema}`, inputs);
  }
  setSchemaState(newSchema);
};

export const hdlChg = ({
  e,
  entirePathData,
  entirePathDataWithKey,
  params,
  entireFormData,
  setFormData,
}) => {
  const newFromData = _.cloneDeep(entireFormData);
  if (e.target.type === "checkbox") {
    _.set(newFromData, entirePathDataWithKey, e.target.checked);
  } else if (e.target.type === "file") {
    _.set(newFromData, entirePathDataWithKey, e.target.files);
  } else if (typeof e.target?.type === "string" && e.target.type.includes("date-range")) {
    if (e.target.type.includes("start")) {
      let dates = _.get(newFromData, entirePathDataWithKey);
      dates = dates.split("__");
      if (dates.length === 2) {
        dates[0] = e.target.value.toJSON();
        dates = dates.join("__");
        _.set(newFromData, entirePathDataWithKey, dates);
      }
    } else if (e.target.type.includes("end")) {
      let dates = _.get(newFromData, entirePathDataWithKey);
      dates = dates.split("__");
      if (dates.length === 2) {
        dates[1] = e.target.value.toJSON();
        dates = dates.join("__");
        _.set(newFromData, entirePathDataWithKey, dates);
      }
    }
  } else if (e.target?.type === "involved") {
    if (e.target.action === "add") {
      const currentInvolveds = _.get(newFromData, entirePathDataWithKey);
      currentInvolveds.push(e.target.value);
    }
    if (e.target.action === "delete") {
      const currentInvolveds = _.get(newFromData, entirePathDataWithKey);
      currentInvolveds.splice(e.target.idx, 1);
    }
  } else if (params && "catalogue" in params && "isOwn" in params) {
    _.set(newFromData, `${entirePathData}.${params.catalogue}`, e.target.value);
  } else {
    _.set(newFromData, entirePathDataWithKey, e.target.value);
  }
  if (params && "conditionals" in params) {
    _.set(newFromData, `${entirePathData}.conditionals`, params.conditionals);
  }
  if (params && "typeOther" in params) {
    _.set(newFromData, `${entirePathData}.typeOther`, params.typeOther);
  }
  setFormData(newFromData);
};

export const hdlIsVal = ({
  errors,
  entirePathDataWithKey,
  entireIsValid,
  setIsValid,
}) => {
  const newIsValid = _.cloneDeep(entireIsValid);
  _.set(newIsValid, `${entirePathDataWithKey}`, errors);
  setIsValid(newIsValid);
};

export const hdlConstraints = ({
  origin,
  value,
  constraints,
  setConstraints,
}) => {
  const newConstraints = constraints.map((item) => {
    if (item.origin == origin) {
      if (value.startsWith(item.value)) {
        return { ...item, selected: true };
      } else {
        return { ...item, selected: false };
      }
    }
    return item;
  });
  setConstraints(newConstraints);
};

// Error messages component
const ReturnErrorMessages = ({ tryNext, valid }) => {
  return (
    <>
      {tryNext &&
        Array.isArray(valid) &&
        valid?.length > 0 &&
        valid.map((err, idx) => (
          <div key={idx} className="eg-invalid-msg">
            {err}
          </div>
        ))}
    </>
  );
};

// Involved persons input component
const FormInputInvolved = ({ schema, value, valid, br, origin, tryNext, handleChange, lang }) => {
  const i18n = {
    "involved_name_placeholder": I18nText({ lang, translation: "involved_name_placeholder" }),
    "involved_occupation_placeholder": I18nText({ lang, translation: "involved_occupation_placeholder" }),
    "involved_type_placeholder": I18nText({ lang, translation: "involved_type_placeholder" }),
    "option_victim": I18nText({ lang, translation: "option_victim" }),
    "option_witness": I18nText({ lang, translation: "option_witness" }),
    "option_offender": I18nText({ lang, translation: "option_offender" }),
    "option_accomplice": I18nText({ lang, translation: "option_accomplice" }),
    "involved_addBtn": I18nText({ lang, translation: "involved_addBtn" }),
    "victim": I18nText({ lang, translation: "victim" }),
    "witness": I18nText({ lang, translation: "witness" }),
    "offender": I18nText({ lang, translation: "offender" }),
    "accomplice": I18nText({ lang, translation: "accomplice" }),
    "not_involved_added": I18nText({ lang, translation: "not_involved_added" }),
  };

  const [nameInvolved, setNameInvolved] = useState("");
  const [occupationInvolved, setOccupationInvolved] = useState("");
  const [tryToAdd, setTryToAdd] = useState(false);
  const [typeInvolved, setTypeInvolved] = useState(null);
  const [orderedInvolved, setOrderedInvolved] = useState([]);

  const optionsInvolved = [
    { label: i18n["option_victim"], value: "victim" },
    { label: i18n["option_witness"], value: "witness" },
    { label: i18n["option_offender"], value: "offender" },
    { label: i18n["option_accomplice"], value: "accomplice" },
  ];

  useEffect(() => {
    if (!Array.isArray(value)) return;

    const tempVictims = [];
    const tempWitnesses = [];
    const tempOffenders = [];
    const tempAccomplice = [];

    for (const [idx, involved] of value.entries()) {
      if (involved.type === "victim") {
        tempVictims.push({ idx, ...involved });
      }
      if (involved.type === "witness") {
        tempWitnesses.push({ idx, ...involved });
      }
      if (involved.type === "offender") {
        tempOffenders.push({ idx, ...involved });
      }
      if (involved.type === "accomplice") {
        tempAccomplice.push({ idx, ...involved });
      }
    }

    setOrderedInvolved([...tempVictims, ...tempWitnesses, ...tempOffenders, ...tempAccomplice]);
  }, [value]);

  const hdlAddInvolved = () => {
    setTryToAdd(true);
    if (nameInvolved.trim() === "") return;
    if (occupationInvolved.trim() === "") return;
    if (typeInvolved === null) return;
    handleChange({
      target: {
        name: schema.key,
        type: "involved",
        action: "add",
        value: {
          type: typeInvolved,
          name: nameInvolved,
          occupation: occupationInvolved,
        },
      },
    });
    setNameInvolved("");
    setOccupationInvolved("");
    setTryToAdd(false);
  };

  const hdlDeleteInvolved = (idx) => {
    handleChange({
      target: {
        name: schema.key,
        type: "involved",
        action: "delete",
        idx
      },
    });
  };

  const returnIsValid = (input) => {
    if (!tryToAdd) return true;
    if (input === "name" && nameInvolved.trim() === "") return false;
    if (input === "occupation" && occupationInvolved.trim() === "") return false;
    if (input === "type" && typeInvolved === null) return false;
    return true;
  };

  return (
    <>
      <EgCol md={schema.grid || 12} className="eg-preview-input-container eg-form-input-involved">
        <EgLabel required={schema.required}>
          {schema.locales?.[lang] === undefined ? schema.label : schema.locales[lang]}
        </EgLabel>

        <div className="eg-form-input-involved__add-involved">
          <div className="eg-flex-grow-1">
            <EgInput
              value={nameInvolved}
              onChange={(e) => setNameInvolved(e.target.value)}
              autoComplete="off"
              type="text"
              isValid={returnIsValid("name")}
              name={`${origin}-involved-name`}
              placeholder={i18n["involved_name_placeholder"]}
            />
          </div>

          <div className="eg-flex-grow-1">
            <EgInput
              value={occupationInvolved}
              autoComplete="off"
              type="text"
              isValid={returnIsValid("occupation")}
              name={`${origin}-involved-occupation`}
              onChange={(e) => setOccupationInvolved(e.target.value)}
              placeholder={i18n["involved_occupation_placeholder"]}
            />
          </div>

          <div className="eg-flex-grow-1">
            <EgSelect
              type="text"
              name={`${origin}-involved-type`}
              options={optionsInvolved}
              isValid={returnIsValid("type")}
              value={optionsInvolved.find(item => item.value === typeInvolved)}
              onChange={(e) => setTypeInvolved(e.value)}
              placeholder={i18n["involved_type_placeholder"]}
            />
          </div>

          <div>
            <EgButton
              type="button"
              variant="primary"
              outline
              onClick={() => hdlAddInvolved(nameInvolved, occupationInvolved, typeInvolved)}
            >
              {i18n["involved_addBtn"]}
            </EgButton>
          </div>
        </div>

        <div className="eg-people-involved">
          {(orderedInvolved.length >= 1) ? (
            <div className="eg-selectable-list">
              {orderedInvolved.map((item, idx) => (
                <div key={idx} className={`eg-selectable-item eg-${item.type}`}>
                  <strong>{i18n[`option_${item.type}`]}:&nbsp;</strong>
                  {item.name} ({item.occupation})
                  <button className="eg-delete-btn" onClick={() => hdlDeleteInvolved(item.idx)}>X</button>
                </div>
              ))}
            </div>
          ) : (
            <div className="eg-not-involved">
              {i18n["involved_type_placeholder"]}
            </div>
          )}
        </div>

        <ReturnErrorMessages tryNext={tryNext} valid={valid} />
      </EgCol>
      {br && <div></div>}
    </>
  );
};

// Import isolated catalogue components
import { WidgetCatalogueInput } from "./components/WidgetCatalogueInput";
import { WidgetConditionalInputs } from "./components/WidgetConditionalInputs";
import { WidgetCatalogueTypeOther } from "./components/WidgetCatalogueTypeOther";

// Render input component
export const WidgetRenderInput = ({
  entireSchema,
  entirePathSchema,
  setSchemaState,
  entireFormData,
  entirePathData,
  setFormData,
  entireIsValid,
  setIsValid,
  constraints,
  setConstraints,
  attachments,
  setAttachments,
  tryToNext,
  activeStep,
  origin,
  answered,
  lang,
  theme = 'light',
  formParams = {}
}) => {
  const inputAttachment = useRef(null);

  const i18n = {
    "form_requiered": I18nText({ lang, translation: "form_requiered" }),
    "form_check_required": I18nText({ lang, translation: "form_check_required" }),
    "form_file_required": I18nText({ lang, translation: "form_file_required" }),
    "form_invalid_date": I18nText({ lang, translation: "form_invalid_date" }),
    "form_invalid_initial_date": I18nText({ lang, translation: "form_invalid_initial_date" }),
    "form_invalid_finish_date": I18nText({ lang, translation: "form_invalid_finish_date" }),
    "form_invalid_field": I18nText({ lang, translation: "form_invalid_field" }),
    "form_invalid_email": I18nText({ lang, translation: "form_invalid_email" }),
    "input_file_drag_drop_files": I18nText({ lang, translation: "input_file_drag_drop_files" }),
    "input_file_or_click_here": I18nText({ lang, translation: "input_file_or_click_here" }),
    "input_date_range_start_date": I18nText({ lang, translation: "input_date_range_start_date" }),
    "input_date_range_end_date": I18nText({ lang, translation: "input_date_range_end_date" }),
  };

  const schema = _.get(entireSchema, entirePathSchema);
  if (schema === undefined) return null;

  const key = schema.type.includes(CATALOGUES_PREFIX)
    ? schema.catalogue
    : schema.key;

  const entirePathDataWithKey = `${entirePathData}.${key}`;
  const value = _.get(entireFormData, entirePathDataWithKey);
  const valid = _.get(entireIsValid, entirePathDataWithKey);
  const tryNext = _.get(tryToNext, activeStep);
  const br = schema.break;

  const utilHdlChg = {
    entireFormData,
    entirePathData,
    entirePathDataWithKey,
    setFormData,
  };

  const doValidate = (test, initType, initRequired) => {
    let errors = [];
    const type = initType ?? schema.type;
    const required = initRequired ?? schema.required;

    if (type === INPUT_TYPE_TEXT) {
      if (required === true) {
        if (test.trim() === "") {
          errors.push(i18n["form_requiered"]);
        }
      }
    }
    if (type === INPUT_TYPE_NUMBER) {
      const tempNum = Number(test);
      if (required === true) {
        if (test.trim() === "") {
          errors.push(i18n["form_requiered"]);
        } else if (isNaN(tempNum)) {
          errors.push(i18n["form_invalid_field"]);
        } else if (tempNum < 0) {
          errors.push(i18n["form_invalid_field"]);
        }
      } else {
        if (isNaN(tempNum)) {
          errors.push(i18n["form_invalid_field"]);
        } else if (tempNum < 0) {
          errors.push(i18n["form_invalid_field"]);
        }
      }
    }
    if (type === INPUT_TYPE_TEXTAREA) {
      if (required === true) {
        if (test.trim() === "") {
          errors.push(i18n["form_check_required"]);
        }
      }
    }
    if (type.includes(INPUT_TYPE_CHECKBOX)) {
      if (required === true) {
        if (test === false) {
          errors.push(i18n["form_check_required"]);
        }
      }
    }
    if (type === INPUT_TYPE_FILE) {
      if (required === true) {
        const formDataEntries = test.entries();
        const length = Array.from(formDataEntries).length;
        if (test === null) {
          errors.push(i18n["form_file_required"]);
        }
      }
    }
    if (type === INPUT_TYPE_SUBJECT) {
      if (required === true) {
        if (test.trim() === "") {
          errors.push(i18n["form_requiered"]);
        }
      }
    }
    if (type === INPUT_TYPE_DESCRIPTION) {
      if (required === true) {
        if (test.trim() === "") {
          errors.push(i18n["form_requiered"]);
        }
      }
    }
    if (type === INPUT_TYPE_EMAIL) {
      const regex = /^[\w-\.]+@([\w-]+\.)+[\w-]{2,}$/;
      if (required === true) {
        if (!regex.test(test)) {
          errors.push(i18n["form_invalid_email"]);
        }
      } else {
        if (test.trim() !== "" && !regex.test(test)) {
          errors.push(i18n["form_invalid_email"]);
        }
      }
    }
    if (type.includes(CATALOGUES_PREFIX)) {
      if (required) {
        if (!test) {
          errors.push(i18n["form_requiered"]);
        }
      }
    }
    return errors;
  };

  const handleChange = (e) => {
    hdlChg({
      e,
      params: { sensitive: schema.sensitive, origin },
      ...utilHdlChg,
    });
    const val = schema.type.includes(INPUT_TYPE_CHECKBOX)
      ? e.target.checked
      : e.target.value;
    const errors = doValidate(val);
    if (
      typeof schema.type.includes(INPUT_TYPE_CHECKBOX) &&
      !schema.type.includes(CATALOGUES_PREFIX)
    ) {
      hdlIsVal({ errors, entirePathDataWithKey, entireIsValid, setIsValid });
    }
  };

  const handleValidate = (validCat) => {
    const errors = doValidate(validCat);
    hdlIsVal({ errors, entirePathDataWithKey, entireIsValid, setIsValid });
  };

  const returnValidClass = (type) => {
    if (
      Array.isArray(valid) &&
      valid.length > 0 &&
      tryNext &&
      type === "container"
    ) {
      return "eg-is-invalid-container";
    }
    if (Array.isArray(valid) && valid.length > 0 && tryNext) {
      return "eg-is-invalid";
    }
    return "";
  };

  // File attachments handlers
  const hdlAttch = (e) => {
    let formData;
    if (!attachments) {
      formData = new FormData();
    } else {
      formData = attachments;
    }
    for (let i = 0; i < e.target.files.length; i++) {
      if (!answered) {
        formData.append("attachments", e.target.files[i]);
      } else {
        formData.append("file", e.target.files[i]);
      }
    }
    const errors = doValidate(formData);
    hdlIsVal({ errors, entirePathDataWithKey, entireIsValid, setIsValid });
    setAttachments(formData);
  };

  const hdlDragOvAttch = (e) => {
    e.preventDefault();
  };

  const hdlDropAttch = (e) => {
    e.preventDefault();
    let formData;
    if (!attachments) {
      formData = new FormData();
    } else {
      formData = attachments;
    }
    for (let i = 0; i < e.dataTransfer.files.length; i++) {
      if (!answered) {
        formData.append("attachments", e.dataTransfer.files[i]);
      } else {
        formData.append("file", e.dataTransfer.files[i]);
      }
    }
    const errors = doValidate(formData);
    hdlIsVal({ errors, entirePathDataWithKey, entireIsValid, setIsValid });
    setAttachments(formData);
  };

  const hdlDeleteAttch = (position) => {
    let newFormData = new FormData();
    let index = 0;
    attachments.forEach((value, currentKey) => {
      if (index !== position) {
        newFormData.append(currentKey, value);
      }
      index++;
    });

    const errors = doValidate(newFormData);
    hdlIsVal({ errors, entirePathDataWithKey, entireIsValid, setIsValid });
    setAttachments(newFormData);
  };

  // Date validation
  const isValidDate = (startend) => {
    if (startend) {
      let dates = value;
      dates = dates.split("__");
      if (startend === "start" && dates.length === 2) {
        return !isNaN(new Date(dates[0]).getTime());
      } else if (startend === "end" && dates.length === 2) {
        return !isNaN(new Date(dates[1]).getTime());
      }
    }
    return !isNaN(new Date(value).getTime());
  };

  const returnDateRange = (startend) => {
    let dates = value;
    dates = dates.split("__");
    if (startend === "start" && dates.length === 2) {
      return new Date(dates[0]);
    } else if (startend === "end" && dates.length === 2) {
      return new Date(dates[1]);
    }
    return false;
  };

  const translate = () => {
    if (schema.key === "specify_other") {
      return i18n["please_specify"];
    }
    return schema.locales?.[lang] === undefined
      ? schema.label
      : schema.locales[lang];
  };

  if (schema.hidden && !schema.catalogue && isForLanding) return null;

  // TEXT INPUT
  if (schema.type === INPUT_TYPE_TEXT) {
    return (
      <>
        <EgCol md={schema.grid || 12} className="eg-preview-input-container">
          <div className="eg-flex">
            <EgLabel required={schema.required}>
              {translate()}
            </EgLabel>
            {schema.hidden && !isForLanding && (
              <WidgetIcon className="eg-ms-2" name="eye_slash" size={16} />
            )}
          </div>
          <div className={`eg-preview-input-container-inp ${returnValidClass("container")}`}>
            <EgInput
              autoComplete="off"
              type="text"
              className={returnValidClass()}
              name={schema.key}
              defaultValue={value}
              onChange={handleChange}
              placeholder={schema.placeholder}
            />
          </div>
          <ReturnErrorMessages tryNext={tryNext} valid={valid} />
        </EgCol>
        {br && <div></div>}
      </>
    );
  }

  // NUMBER INPUT
  if (schema.type === INPUT_TYPE_NUMBER) {
    return (
      <>
        <EgCol md={schema.grid || 12} className="eg-preview-input-container">
          <div className="eg-flex">
            <EgLabel required={schema.required}>
              {schema.locales?.[lang] === undefined ? schema.label : schema.locales[lang]}
            </EgLabel>
            {schema.hidden && !isForLanding && (
              <WidgetIcon className="eg-ms-2" name="eye_slash" size={16} />
            )}
          </div>
          <div className={`eg-preview-input-container-inp ${returnValidClass("container")}`}>
            <EgInput
              autoComplete="off"
              type="number"
              className={returnValidClass()}
              name={schema.key}
              defaultValue={value}
              onChange={handleChange}
              placeholder={schema.placeholder}
            />
          </div>
          <ReturnErrorMessages tryNext={tryNext} valid={valid} />
        </EgCol>
        {br && <div></div>}
      </>
    );
  }

  // TEXTAREA INPUT
  if (schema.type === INPUT_TYPE_TEXTAREA) {
    return (
      <>
        <EgCol md={schema.grid || 12} className="eg-preview-input-container">
          <div className="eg-flex">
            <EgLabel required={schema.required}>
              {schema.locales?.[lang] === undefined ? schema.label : schema.locales[lang]}
            </EgLabel>
            {schema.hidden && !isForLanding && (
              <WidgetIcon className="eg-ms-2" name="eye_slash" size={16} />
            )}
          </div>
          <div className={`eg-preview-input-container-inp ${returnValidClass("container")}`}>
            <EgInput
              type="textarea"
              rows={3}
              autoComplete="off"
              className={returnValidClass()}
              name={schema.key}
              defaultValue={value}
              onChange={handleChange}
              placeholder={schema.placeholder}
            />
          </div>
          <ReturnErrorMessages tryNext={tryNext} valid={valid} />
        </EgCol>
        {br && <div></div>}
      </>
    );
  }

  // CHECKBOX INPUT
  if (schema.type === INPUT_TYPE_CHECKBOX) {
    return (
      <>
        <EgCol md={schema.grid || 12} className="eg-preview-input-container">
          <div className={`eg-form-check ${returnValidClass("container")} ${schema.defaultAnswer === true ? "eg-ps-0" : ""}`}>
            {!schema.defaultAnswer === true && (
              <EgCheckbox
                id={origin}
                name={schema.key}
                defaultChecked={value}
                onChange={handleChange}
              />
            )}
            <div className="eg-flex">
              <EgLabel required={schema.required} htmlFor={origin} className="eg-form-check-label">
                {schema.locales?.[lang] === undefined ? schema.label : schema.locales[lang]}
              </EgLabel>
              {schema.hidden && !isForLanding && (
                <WidgetIcon className="eg-ms-2" name="eye_slash" size={16} />
              )}
            </div>
          </div>
          <ReturnErrorMessages tryNext={tryNext} valid={valid} />
        </EgCol>
        {br && <div></div>}
      </>
    );
  }

  // CHECKBOX CONDITIONAL
  if (schema.type === INPUT_TYPE_CHECKBOX_CONDITIONAL) {
    return (
      <>
        <EgCol md={schema.grid || 12} className="eg-preview-input-container">
          <div className={`eg-form-check ${returnValidClass("container")}`}>
            <EgCheckbox
              name={schema.key}
              id={schema.key}
              defaultChecked={value}
              onChange={handleChange}
            />
            <div className="eg-flex">
              <EgLabel required={schema.required} htmlFor={schema.key} className="eg-form-check-label">
                {schema.locales?.[lang] === undefined ? schema.label : schema.locales[lang]}
              </EgLabel>
              {schema.hidden && !isForLanding && (
                <WidgetIcon className="eg-ms-2" name="eye_slash" size={16} />
              )}
            </div>
          </div>
          <ReturnErrorMessages tryNext={tryNext} valid={valid} />
        </EgCol>
        {br && <div></div>}
        <WidgetConditionalInputs
          RenderInputComponent={WidgetRenderInput}
          entireSchema={entireSchema}
          entirePathSchema={entirePathSchema}
          setSchemaState={setSchemaState}
          entireFormData={entireFormData}
          entirePathData={entirePathData}
          setFormData={setFormData}
          entireIsValid={entireIsValid}
          setIsValid={setIsValid}
          doValidate={doValidate}
          conditionals={schema.conditionals}
          parentValue={value}
          parentOrigin={origin}
          attachments={attachments}
          setAttachments={setAttachments}
          tryToNext={tryToNext}
          activeStep={activeStep}
          answered={answered}
          lang={lang}
          formParams={formParams}
        />
      </>
    );
  }

  // FILE INPUT
  if (schema.type === INPUT_TYPE_FILE) {
    return (
      <>
        <EgCol
          md={schema.grid || 12}
          className="eg-preview-input-container"
          onDrop={hdlDropAttch}
          onDragOver={hdlDragOvAttch}
        >
          <div className="eg-flex">
            <EgLabel required={schema.required}>
              {schema.locales?.[lang] === undefined ? schema.label : schema.locales[lang]}
            </EgLabel>
            {schema.hidden && !isForLanding && (
              <WidgetIcon className="eg-ms-2" name="eye_slash" size={16} />
            )}
          </div>
          <div
            className="eg-attachments eg-pointer"
            onClick={() => inputAttachment.current.click()}
          >
            <div className="eg-attachments-icon-container">
              <WidgetIcon name="gallery" />
              <WidgetIcon name="clipboard" />
              <WidgetIcon name="audio" />
              <WidgetIcon name="video" />
              <WidgetIcon name="document" />
            </div>

            <div className="eg-attachments-instructions">
              <p>{i18n["input_file_drag_drop_files"]}</p>
              <button className="eg-btn eg-attachments-btn">
                {i18n["input_file_or_click_here"]}
              </button>
            </div>
          </div>
          <input
            className="eg-file-input-hidden"
            name={schema.key}
            type="file"
            onChange={hdlAttch}
            multiple
            ref={inputAttachment}
            style={{ display: 'none' }}
          />
          {attachments !== null && [...attachments.entries()].length > 0 && (
            <ul className="eg-attachments-list">
              {[...attachments.entries()].map((item, idx) => {
                return (
                  <li key={idx} className="eg-attachment-item">
                    <button
                      type="button"
                      className="eg-attachment-delete"
                      onClick={() => hdlDeleteAttch(idx)}
                      title="Eliminar"
                    >
                      <WidgetIcon name="delete" size={14} />
                    </button>
                    <span className="eg-attachment-name">{item[1].name}</span>
                  </li>
                );
              })}
            </ul>
          )}
          <ReturnErrorMessages tryNext={tryNext} valid={valid} />
        </EgCol>
        {br && <div></div>}
      </>
    );
  }

  // DATE INPUT
  if (schema.type === INPUT_TYPE_DATE) {
    const initialDate = new Date();
    return (
      <>
        <EgCol md={schema.grid || 12} className="eg-preview-input-container">
          <div className={returnValidClass("container")}>
            <div className="eg-flex">
              <EgLabel required={schema.required}>
                {schema.locales?.[lang] === undefined ? schema.label : schema.locales[lang]}
              </EgLabel>
              {schema.hidden && !isForLanding && (
                <WidgetIcon className="eg-ms-2" name="eye_slash" size={16} />
              )}
            </div>
            <div className="eg-preview-input-container-inp">
              <EgInput
                type="date"
                className={returnValidClass()}
                value={value && isValidDate() ? value : initialDate}
                lang={lang}
                onChange={(date) =>
                  handleChange({
                    target: {
                      name: schema.key,
                      value: date,
                      type: "date"
                    },
                  })
                }
              />
            </div>
          </div>
          <ReturnErrorMessages tryNext={tryNext} valid={valid} />
        </EgCol>
        {br && <div></div>}
      </>
    );
  }

  // DATE RANGE INPUT
  if (schema.type === INPUT_TYPE_DATE_RANGE) {
    const initialDate = new Date();
    return (
      <>
        <EgCol md={schema.grid || 12} className="eg-preview-input-container">
          <div className={returnValidClass("container")}>
            <EgLabel required={schema.required}>
              {schema.locales?.[lang] === undefined ? schema.label : schema.locales[lang]}
            </EgLabel>
            <EgRow className="eg-p-0">
              <EgCol md={6} className="eg-ps-lg-0">
                <label className="eg-label">{i18n["input_date_range_start_date"]}:</label>
                <div className="eg-preview-input-container-inp">
                  <EgInput
                    type="date"
                    className={returnValidClass()}
                    value={isValidDate("start") ? returnDateRange("start") : initialDate}
                    lang={lang}
                    onChange={(date) =>
                      handleChange({
                        target: {
                          name: schema.key,
                          value: date,
                          type: "date-range-start",
                        },
                      })
                    }
                  />
                </div>
              </EgCol>
              <EgCol md={6} className="eg-pe-lg-0">
                <label className="eg-label">{i18n["input_date_range_end_date"]}:</label>
                <div className="eg-preview-input-container-inp">
                  <EgInput
                    type="date"
                    className={returnValidClass()}
                    value={isValidDate("end") ? returnDateRange("end") : initialDate}
                    lang={lang}
                    onChange={(date) =>
                      handleChange({
                        target: {
                          name: schema.key,
                          value: date,
                          type: "date-range-end",
                        },
                      })
                    }
                  />
                </div>
              </EgCol>
            </EgRow>
          </div>
        </EgCol>
        {br && <div></div>}
      </>
    );
  }

  // SUBJECT INPUT
  if (schema.type === INPUT_TYPE_SUBJECT) {
    return (
      <>
        <EgCol md={schema.grid || 12} className="eg-preview-input-container">
          <div className="eg-flex">
            <EgLabel required={schema.required}>
              {schema.locales?.[lang] === undefined ? schema.label : schema.locales[lang]}
            </EgLabel>
            {schema.hidden && !isForLanding && (
              <WidgetIcon className="eg-ms-2" name="eye_slash" size={16} />
            )}
          </div>
          <div className={`eg-preview-input-container-inp ${returnValidClass("container")}`}>
            <EgInput
              autoComplete="off"
              type="text"
              className={returnValidClass()}
              name={schema.key}
              defaultValue={value}
              onChange={handleChange}
              placeholder={schema.placeholder}
            />
          </div>
          <ReturnErrorMessages tryNext={tryNext} valid={valid} />
        </EgCol>
        {br && <div></div>}
      </>
    );
  }

  // DESCRIPTION INPUT
  if (schema.type === INPUT_TYPE_DESCRIPTION) {
    return (
      <>
        <EgCol md={schema.grid || 12} className="eg-preview-input-container">
          <div className="eg-flex">
            <EgLabel required={schema.required}>
              {schema.locales?.[lang] === undefined ? schema.label : schema.locales[lang]}
            </EgLabel>
            {schema.hidden && !isForLanding && (
              <WidgetIcon className="eg-ms-2" name="eye_slash" size={16} />
            )}
          </div>
          <div className={`eg-preview-input-container-inp ${returnValidClass("container")}`}>
            <EgInput
              type="textarea"
              autoComplete="off"
              className={returnValidClass()}
              name={schema.key}
              defaultValue={value}
              onChange={handleChange}
              placeholder={schema.placeholder}
              rows="10"
            />
          </div>
          <ReturnErrorMessages tryNext={tryNext} valid={valid} />
        </EgCol>
        {br && <div></div>}
      </>
    );
  }

  // EMAIL INPUT
  if (schema.type === INPUT_TYPE_EMAIL) {
    return (
      <>
        <EgCol md={schema.grid || 12} className="eg-preview-input-container">
          <div className="eg-flex">
            <EgLabel required={schema.required}>
              {schema.locales?.[lang] === undefined ? schema.label : schema.locales[lang]}
            </EgLabel>
            {schema.hidden && !isForLanding && (
              <WidgetIcon className="eg-ms-2" name="eye_slash" size={16} />
            )}
          </div>
          <div className={`eg-preview-input-container-inp ${returnValidClass("container")}`}>
            <EgInput
              type="text"
              autoComplete="off"
              className={returnValidClass()}
              name={schema.key}
              defaultValue={value}
              onChange={handleChange}
              placeholder={schema.placeholder}
            />
          </div>
          <ReturnErrorMessages tryNext={tryNext} valid={valid} />
        </EgCol>
        {br && <div></div>}
      </>
    );
  }

  // INVOLVED INPUT
  if (schema.type === INPUT_TYPE_INVOLVED) {
    return (
      <FormInputInvolved
        schema={schema}
        origin={origin}
        tryNext={tryNext}
        valid={valid}
        value={value}
        br={br}
        handleChange={handleChange}
        lang={lang}
      />
    );
  }

  // CATALOGUE INPUTS
  if (
    schema.type === INPUT_TYPE_CATALOGUE_SELECT ||
    schema.type === INPUT_TYPE_CATALOGUE_RADIO ||
    schema.type === INPUT_TYPE_CATALOGUE_RADIO_DESCRIPTION
  ) {
    return (
      <>
        <WidgetCatalogueInput
          schema={schema}
          scope={schema}
          value={value}
          valid={valid}
          origin={origin}
          pathSchema={entirePathSchema}
          pathData={entirePathData}
          entireSchema={entireSchema}
          entireFormData={entireFormData}
          setSchemaState={setSchemaState}
          setFormData={setFormData}
          constraints={constraints}
          setConstraints={setConstraints}
          tryToNext={tryNext}
          handleValidate={handleValidate}
          returnValidClass={returnValidClass}
          containerDefault={schema?.container_default}
          lang={lang}
          formParams={formParams}
        />
        <WidgetCatalogueTypeOther
          RenderInputComponent={WidgetRenderInput}
          entireSchema={entireSchema}
          entirePathSchema={entirePathSchema}
          setSchemaState={setSchemaState}
          entireFormData={entireFormData}
          entirePathData={entirePathData}
          setFormData={setFormData}
          entireIsValid={entireIsValid}
          setIsValid={setIsValid}
          doValidate={doValidate}
          constraints={constraints}
          setConstraints={setConstraints}
          parentValue={value}
          parentOrigin={origin}
          attachments={attachments}
          setAttachments={setAttachments}
          tryToNext={tryToNext}
          activeStep={activeStep}
          formParams={formParams}
        />
        {br && <div></div>}
      </>
    );
  }

  // CATALOGUE CONDITIONAL INPUTS
  if (
    schema.type === INPUT_TYPE_CATALOGUE_SELECT_CONDITIONAL ||
    schema.type === INPUT_TYPE_CATALOGUE_RADIO_CONDITIONAL ||
    schema.type === INPUT_TYPE_CATALOGUE_RADIO_DESCRIPTION_CONDITIONAL
  ) {
    return (
      <>
        <WidgetCatalogueInput
          schema={schema}
          scope={schema}
          value={value}
          valid={valid}
          origin={origin}
          pathSchema={entirePathSchema}
          pathData={entirePathData}
          entireSchema={entireSchema}
          entireFormData={entireFormData}
          setSchemaState={setSchemaState}
          setFormData={setFormData}
          constraints={constraints}
          setConstraints={setConstraints}
          tryToNext={tryNext}
          handleValidate={handleValidate}
          returnValidClass={returnValidClass}
          containerDefault={schema?.container_default}
          lang={lang}
          formParams={formParams}
        />
        <WidgetCatalogueTypeOther
          RenderInputComponent={WidgetRenderInput}
          entireSchema={entireSchema}
          entirePathSchema={entirePathSchema}
          setSchemaState={setSchemaState}
          entireFormData={entireFormData}
          entirePathData={entirePathData}
          setFormData={setFormData}
          entireIsValid={entireIsValid}
          setIsValid={setIsValid}
          doValidate={doValidate}
          constraints={constraints}
          setConstraints={setConstraints}
          parentValue={value}
          parentOrigin={origin}
          attachments={attachments}
          setAttachments={setAttachments}
          tryToNext={tryToNext}
          activeStep={activeStep}
          formParams={formParams}
        />
        {br && <div></div>}
        <WidgetConditionalInputs
          RenderInputComponent={WidgetRenderInput}
          entireSchema={entireSchema}
          entirePathSchema={entirePathSchema}
          setSchemaState={setSchemaState}
          entireFormData={entireFormData}
          entirePathData={entirePathData}
          setFormData={setFormData}
          entireIsValid={entireIsValid}
          setIsValid={setIsValid}
          doValidate={doValidate}
          conditionals={schema.conditionals}
          constraints={constraints}
          setConstraints={setConstraints}
          parentValue={value}
          parentOrigin={origin}
          attachments={attachments}
          setAttachments={setAttachments}
          tryToNext={tryToNext}
          activeStep={activeStep}
          answered={answered}
          lang={lang}
          formParams={formParams}
        />
      </>
    );
  }

  if (schema.type === INPUT_TYPE_CATALOGUE_ERROR) {
    return null;
  }

  return null;
};

// Main Widget Preview Form Component
export const WidgetPreviewForm = ({
  steps,
  schemaState,
  formData,
  isValid,
  setSchemaState,
  setFormData,
  setIsValid,
  constraints,
  setConstraints,
  showButtons = true,
  stepClick = false,
  lang: langForced,
  content,
  fullscreen = false,
  answered = false,
  id = null,
  setTrackingCodeForm = null,
  theme = 'light',
  // Widget-specific props
  apiClient,
  onSubmit: onSubmitCallback,
  onError: onErrorCallback,
  formIdentifier,
  formParams = {},
}) => {
  // Language
  const lang = langForced || localStorage.getItem("eg_lang") || "es";

  const i18n = {
    "step_no_name": I18nText({ lang, translation: "step_no_name" }),
    "step_without_name": I18nText({ lang, translation: "step_without_name" }),
    "form_next_button": I18nText({ lang, translation: "form_next_button" }),
    "form_prev_button": I18nText({ lang, translation: "form_prev_button" }),
    "form_send_button": I18nText({ lang, translation: "form_send_button" }),
    "form_success_button": I18nText({ lang, translation: "form_success_button" }),
    "form_cancel_button": I18nText({ lang, translation: "form_cancel_button" }),
    "form_create_new_report": I18nText({ lang, translation: "form_create_new_report" }),
    "form_go_reports": I18nText({ lang, translation: "form_go_reports" }),
    "form_success": content?.form_success ? content.form_success : I18nText({ lang, translation: "form_success" }),
    "form_tracking": content?.form_tracking ? content.form_tracking : I18nText({ lang, translation: "form_tracking" }),
    "form_attachment_error_title": I18nText({ lang, translation: "form_attachment_error_title" }),
    "form_attachment_error_message": I18nText({ lang, translation: "form_attachment_error_message" }),
    "form_attachment_error_continue": I18nText({ lang, translation: "form_attachment_error_continue" }),
    "form_attachment_error_cancel": I18nText({ lang, translation: "form_attachment_error_cancel" }),
  };

  // State
  const [success, setSuccess] = useState(false);
  const [trackingCode, setTrackingCode] = useState("XXX XXX XXX");
  const [email, setEmail] = useState("");
  const [sendEmail, setSendEmail] = useState(false);
  const [waitSend, setWaitSend] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [tryToNext, setTryToNext] = useState(null);
  const [attachments, setAttachments] = useState(null);
  const formRef = useRef(null);

  // Use provided API client or create one
  const api = apiClient || createWidgetApiClient(localStorage.getItem('eg_domain') || 'lineaetica.com');

  useEffect(() => {
    const newTryToNext = steps?.map((item) => false) || [];
    setTryToNext(newTryToNext);
  }, [steps]);

  // Generate random folio
  const genFolio = () => {
    const characters = "abcdefghijklmnopqrstuvwxyz0123456789";
    const segmentLength = 4;
    const totalLength = 24;
    let folio = "";

    for (let i = 0; i < totalLength; i++) {
      if (i > 0 && i % segmentLength === 0) {
        folio += "-";
      }
      const randomIndex = Math.floor(Math.random() * characters.length);
      folio += characters.charAt(randomIndex);
    }

    return folio;
  };

  // Generate numeric folio
  const genNumberFolio = (length) => {
    const characters = "0123456789";
    let folio = "";

    for (let i = 0; i < length; i++) {
      const randomIndex = Math.floor(Math.random() * characters.length);
      folio += characters.charAt(randomIndex);
    }

    return folio;
  };

  // reCAPTCHA submit
  const onRecaptchaSubmit = async () => {
    if (!window.grecaptcha || typeof window.grecaptcha.ready !== "function") {
      console.error("reCAPTCHA not available");
      return null;
    }

    try {
      await new Promise((resolve) => window.grecaptcha.ready(resolve));
      const token = await window.grecaptcha.execute('6LdCZ6wqAAAAADNDLaiULssUYnuqIx0MePGwJ_2N', { action: 'submit' });
      return token;
    } catch (error) {
      console.error("reCAPTCHA error:", error);
      return null;
    }
  };

  // Form submission
  const handleSubmit = async () => {
    let isValidForm = handleValidStep({ lastStep: true });

    if (!(isValidForm === false)) {
      try {
        let formAnswers = {};
        var attachments_id = [];

        if (attachments) {
          try {
            const attachmentsRes = await api.createAttachment(attachments);
            attachments_id = attachmentsRes?.attachments_id || [];
          } catch (attachError) {
            console.error('[EthicsGlobal Widget] Attachment upload failed:', attachError);

            const userChoice = await Swal.fire({
              title: `<strong>${ReactDOMServer.renderToString(i18n["form_attachment_error_title"])}</strong>`,
              html: ReactDOMServer.renderToString(i18n["form_attachment_error_message"]),
              icon: 'warning',
              showCancelButton: true,
              confirmButtonColor: '#3085d6',
              cancelButtonColor: '#d33',
              confirmButtonText: i18n["form_attachment_error_continue"],
              cancelButtonText: i18n["form_attachment_error_cancel"],
              allowOutsideClick: false,
              allowEscapeKey: false,
              showCloseButton: false,
            });

            if (!userChoice.isConfirmed) {
              if (onErrorCallback) {
                onErrorCallback(attachError);
              }
              return;
            }

            attachments_id = [];
          }
        }

        for (const [idx, step] of steps.entries()) {
          formAnswers[step.name] = formData[idx];
        }

        const randomFolio = genFolio();
        const objToSend = {
          folio: "FOL-" + randomFolio,
          metadata: formAnswers,
          attachments_id
        };

        if (setTrackingCodeForm) {
          objToSend.tracking_code = setTrackingCodeForm;
        } else {
          objToSend.tracking_code = null;
        }

        if (!waitSend) {
          setWaitSend(true);
          const captchaToken = await onRecaptchaSubmit();
          let formName = formIdentifier || 'report';

          const resp = await api.createReport(objToSend, captchaToken, formName);
          const title = i18n["form_success"];
          const tracking_text = i18n["form_tracking"];

          Swal.fire({
            title: `<strong>${ReactDOMServer.renderToString(title)}</strong>`,
            text: fullscreen ? `${ReactDOMServer.renderToString(tracking_text)} ${resp.tracking_code}` : '',
            showCancelButton: false,
            confirmButtonColor: '#3085d6',
            cancelButtonText: I18nText({ lang: lang, translation: "form_cancel_button" }),
            confirmButtonText: I18nText({ lang: lang, translation: "form_success_button" }),
            allowOutsideClick: false,
            allowEscapeKey: false,
            showCloseButton: false,
          }).then((result) => {
            if (result.isConfirmed) {
              if (fullscreen) {
                window.location.reload();
              } else {
                setTrackingCode(resp.tracking_code);
                setSuccess(true);

                // Call onSubmit callback if provided
                if (onSubmitCallback) {
                  onSubmitCallback({
                    trackingCode: resp.tracking_code,
                    formData: formAnswers
                  });
                }
              }
            }
          });
        }
      } catch (error) {
        Swal.fire('Error', 'An error has occurred while sending the form', 'error');
        console.error(error);
        setWaitSend(false);

        if (onErrorCallback) {
          onErrorCallback(error);
        }
      }
    }
  };

  // Validate step
  const handleValidStep = ({ lastStep }) => {
    const validForNextStep = (inputs) => {
      for (const inp of inputs) {
        const { conditionals, typeOther, ...rest } = inp;
        for (const key in rest) {
          const valor = rest[key];
          if (valor.length > 0) {
            return false;
          }
        }
        if (conditionals) {
          const conditionalsValid = validForNextStep(conditionals);
          if (!conditionalsValid) {
            return false;
          }
        }
        if (typeOther) {
          const typeOtherValid = validForNextStep(typeOther);
          if (!typeOtherValid) {
            return false;
          }
        }
      }
      return true;
    };

    const tempIsValid = _.cloneDeep(isValid[activeStep]);
    const isValidStep = validForNextStep(tempIsValid);

    setTryToNext(
      tryToNext.map((item, idx) => (idx === activeStep ? true : item))
    );

    if (isValidStep) {
      if (!lastStep) {
        setActiveStep(activeStep + 1);
        handleScroll();
      }
      return true;
    } else {
      return false;
    }
  };

  // Scroll to form on step change
  const handleScroll = () => {
    if (formRef.current) {
      const rect = formRef.current.getBoundingClientRect();
      const offset = 50;
      // Don't scroll the main window in widget mode
      formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const [messageCopy, setMessageCopy] = useState(I18nText({ lang, translation: "form_copy_text" }));

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(trackingCode);
      setMessageCopy(I18nText({ lang, translation: "form_copy_copy" }));
    } catch (err) {
      setMessageCopy(I18nText({ lang, translation: "form_copy_error" }));
    }
  };

  const handleEmail = async () => {
    const resp = await api.sendTrackingByEmail({
      "tracking_code": trackingCode,
      "email": email
    });
    alert(I18nText({ lang, translation: "sended_email" }));
  };

  // Build stepper steps
  const stepperSteps = steps?.map((step, index) => ({
    title: step.title || i18n["step_no_name"]
  })) || [];

  // Handle step click in stepper
  const handleStepClick = stepClick ? (index) => setActiveStep(index) : undefined;

  if (!schemaState || !steps) {
    return null;
  }

  return (
    <>
      {success ? (
        // Success screen
        <div className="eg-form-success">
          <EgRow className="eg-form-container eg-p-4">
            <EgCol md={12}>
              <h2 className="eg-text-primary">
                <I18nText lang={lang} translation="form_success" />
              </h2>
            </EgCol>

            <EgCol md={6}>
              <h3 className="eg-text-subtitle">
                <I18nText lang={lang} translation="form_tracking" />
              </h3>

              <div className="eg-tracking-container">
                {!sendEmail ? (
                  <div className="eg-input-with-icon">
                    <p className="eg-tracking-code">{trackingCode}</p>
                    <button onClick={handleCopy} className="eg-copy-btn">
                      <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
                        <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/>
                      </svg>
                    </button>
                  </div>
                ) : (
                  <div className="eg-email-input">
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder={I18nText({ lang, translation: "input_email" })}
                      className="eg-input"
                    />
                    <button onClick={handleEmail} className="eg-send-btn">
                      <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
                        <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
                      </svg>
                    </button>
                  </div>
                )}
              </div>

              {!sendEmail && (
                <p className="eg-copy-message">{messageCopy}</p>
              )}

              <div className="eg-button-container">
                <EgButton
                  variant="primary"
                  outline
                  onClick={() => setSendEmail(!sendEmail)}
                >
                  {sendEmail ? (
                    <I18nText lang={lang} translation="form_show_code" />
                  ) : (
                    <I18nText lang={lang} translation="form_send_by_email" />
                  )}
                </EgButton>
              </div>
            </EgCol>

            <EgCol md={6}>
              <div className="eg-info-container">
                <div className="eg-important-notice">
                  <div className="eg-notice-icon">
                    <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
                      <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/>
                    </svg>
                  </div>
                  <div className="eg-notice-text">
                    <I18nText lang={lang} translation="form_important" />
                  </div>
                </div>
                <p className="eg-important-message">
                  <I18nText lang={lang} translation="form_important_message" />
                </p>
              </div>
            </EgCol>

            <EgCol md={12}>
              <div className="eg-content-info">
                <p className="eg-main-message">
                  <I18nText lang={lang} translation="form_message" />
                </p>
              </div>
            </EgCol>

            <EgCol md={6}>
              <div className="eg-notes-container">
                <div className="eg-note-item">
                  <svg viewBox="0 0 24 24" fill="#f19a15" width="20" height="20">
                    <path d="M11 17h2v-6h-2v6zm1-15C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zM11 9h2V7h-2v2z"/>
                  </svg>
                  <span><I18nText lang={lang} translation="form_ul_one" /></span>
                </div>
                <div className="eg-note-item">
                  <svg viewBox="0 0 24 24" fill="#f19a15" width="20" height="20">
                    <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34a.9959.9959 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
                  </svg>
                  <span><I18nText lang={lang} translation="form_ul_two" /></span>
                </div>
                <div className="eg-note-item">
                  <svg viewBox="0 0 24 24" fill="#f19a15" width="20" height="20">
                    <path d="M7.5 6.5C7.5 8.981 9.519 11 12 11s4.5-2.019 4.5-4.5S14.481 2 12 2 7.5 4.019 7.5 6.5zM20 21h1v-1c0-3.859-3.141-7-7-7h-4c-3.86 0-7 3.141-7 7v1h17z"/>
                  </svg>
                  <span><I18nText lang={lang} translation="form_ul_three" /></span>
                </div>
                <div className="eg-note-item">
                  <svg viewBox="0 0 24 24" fill="#f19a15" width="20" height="20">
                    <path d="M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z"/>
                  </svg>
                  <span><I18nText lang={lang} translation="form_ul_four" /></span>
                </div>
              </div>
            </EgCol>

            <EgCol md={6}>
              <div className="eg-robot-image">
                <img src={ReportRobot} alt="Report Robot" />
              </div>
            </EgCol>
          </EgRow>
        </div>
      ) : (
        // Form screen
        <div className="eg-mt-20" ref={formRef}>
          <EgContainer fluid className="eg-form-container eg-p-5">
            <div className="eg-form-preview">
              {schemaState && (
                <div className="eg-form-preview-inputs eg-animation">
                  {/* Stepper */}
                  <div className="eg-form-preview-header-stepper">
                    <EgStepper
                      steps={stepperSteps}
                      activeStep={activeStep}
                      onStepClick={handleStepClick}
                    />
                  </div>

                  {/* Tab Panels */}
                  <div className="eg-tab-content">
                    {schemaState.map((step, idxStep) => (
                      <div
                        key={idxStep}
                        className={`eg-tab-pane ${activeStep === idxStep ? 'eg-tab-pane-active' : 'eg-tab-pane-hidden'}`}
                      >
                        <div className="eg-form-preview-header eg-mb-30">
                          <div className={`eg-form-preview-header-name eg-indicator-${schemaState.length}-${idxStep + 1}`}>
                            <h3>
                              {steps[idxStep]?.title || i18n["step_without_name"]}
                            </h3>
                            <span className="eg-title-indicator"></span>
                          </div>
                        </div>
                        <EgRow className="eg-form-grid">
                          {step.map((input, idxInput) => (
                            <WidgetRenderInput
                              key={`form-input-${idxStep}-${idxInput}`}
                              entireSchema={schemaState}
                              entireFormData={formData}
                              entireIsValid={isValid}
                              constraints={constraints}
                              setConstraints={setConstraints}
                              entirePathSchema={`${idxStep}.${idxInput}`}
                              entirePathData={`${idxStep}.${idxInput}`}
                              setSchemaState={setSchemaState}
                              setFormData={setFormData}
                              setIsValid={setIsValid}
                              tryToNext={tryToNext}
                              activeStep={idxStep}
                              idx={idxInput}
                              attachments={attachments}
                              setAttachments={setAttachments}
                              origin={`stepers::${idxStep}::form::json-schema::${idxInput}`}
                              lang={lang}
                              answered={answered}
                              theme={theme}
                              formParams={formParams}
                            />
                          ))}
                        </EgRow>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {showButtons && (
              <div className="eg-form-buttons">
                {activeStep !== 0 && (
                  <EgButton
                    variant="primary"
                    className="eg-btn-form eg-btn-prev"
                    onClick={() => setActiveStep((active) => active - 1)}
                  >
                    {i18n["form_prev_button"]}
                  </EgButton>
                )}
                {activeStep === steps.length - 1 ? (
                  <EgButton
                    variant="secondary"
                    className="eg-btn-send"
                    disabled={waitSend}
                    onClick={answered ? () => {} : handleSubmit}
                  >
                    {answered ? "Actualizar" : i18n["form_send_button"]}
                  </EgButton>
                ) : (
                  <EgButton
                    variant="primary"
                    className="eg-btn-form eg-btn-next"
                    onClick={handleValidStep}
                  >
                    {i18n["form_next_button"]}
                  </EgButton>
                )}
              </div>
            )}
          </EgContainer>
        </div>
      )}
    </>
  );
};

export default WidgetPreviewForm;
